import React, { useState, useEffect } from "react";
import { Form, Input, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Link, useSearchParams } from "react-router-dom";
import { resetPassword } from "../../utils/api";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    setIsVisible(true);
  }, []);

  if (!token || !email) {
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-10 shadow-2xl max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Invalid Reset Link</h2>
          <p className="text-gray-400 mb-8">This password reset link is invalid or has expired.</p>
          <Link
            to="/forgot-password"
            className="inline-block py-3 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg"
          >
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  const onFinish = (values) => {
    if (values.newPassword !== values.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError("");
    resetPassword({ email, token, newPassword: values.newPassword })
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          setSuccess(true);
        } else if (res?.response?.data?.message) {
          setError(res.response.data.message);
        } else if (res?.data?.message) {
          setError(res.data.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Something went wrong. Please try again.");
        setLoading(false);
      });
  };

  return (
    <Spin
      spinning={loading}
      indicator={
        <LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />
      }
    >
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative min-h-screen flex items-center justify-center px-4 py-12 pt-32">
          <div
            className={`w-full max-w-md transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-10 shadow-2xl">
              {success ? (
                /* Success State */
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Password Reset Successful</h2>
                  <p className="text-gray-400 mb-8">
                    Your password has been updated. You can now sign in with your new password.
                  </p>
                  <Link
                    to="/login"
                    className="inline-block py-3 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg"
                  >
                    Sign In
                  </Link>
                </div>
              ) : (
                /* Form State */
                <>
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Set New Password</h1>
                    <p className="text-gray-400">Enter your new password below</p>
                  </div>

                  <Form
                    name="resetPassword"
                    onFinish={onFinish}
                    autoComplete="off"
                    layout="vertical"
                    className="space-y-2"
                  >
                    <Form.Item
                      name="newPassword"
                      rules={[
                        { required: true, message: "Please enter a new password" },
                        { min: 6, message: "Password must be at least 6 characters" },
                      ]}
                    >
                      <Input.Password
                        placeholder="New password"
                        className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                        style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      rules={[
                        { required: true, message: "Please confirm your password" },
                      ]}
                    >
                      <Input.Password
                        placeholder="Confirm new password"
                        className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                        style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                      />
                    </Form.Item>

                    {error && (
                      <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 mb-4">
                        <p className="text-red-300 text-sm text-center">{error}</p>
                      </div>
                    )}

                    <Form.Item className="mb-0">
                      <button
                        type="submit"
                        className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                      >
                        Reset Password
                      </button>
                    </Form.Item>
                  </Form>

                  <p className="text-center text-gray-400 mt-8">
                    <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                      Back to Sign In
                    </Link>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default ResetPassword;
